/**
 * Plan Card V2 Component
 *
 * Redesigned plan card with:
 * - Intro pricing display (normally $X crossed out, savings shown)
 * - Categorized benefits with headers
 * - Tooltips with dotted underlines
 * - Disabled state when slider exceeds threshold
 *
 * @module components/billing/PlanCardV2
 */

import { cn } from '@/lib/utils';
import { Button, Tooltip } from '@/components/ui';
import {
  type PlanTier,
  type BillingInterval,
  type UsageBucket,
  getPlanBenefitBuckets,
  formatPrice,
  calculateIntroPrice,
  calculateSavings,
} from '@hummingbird/pricing';

// =============================================================================
// Types
// =============================================================================

export interface PlanCardV2Props {
  /** Plan tier data */
  plan: PlanTier;
  /** Current billing interval */
  interval: BillingInterval;
  /** Bucket for current slider position (null if out of range) */
  bucket: UsageBucket | null;
  /** Currently selected request count */
  selectedRequests: number;
  /** Whether this is the user's current plan AND bucket */
  isCurrentPlan?: boolean;
  /** Whether this plan is highlighted (e.g., "Most Popular") */
  isHighlighted?: boolean;
  /** Whether this plan is disabled (slider exceeds max) */
  isDisabled?: boolean;
  /** Whether user is eligible for intro pricing */
  isEligibleForIntro?: boolean;
  /** Whether this would be a downgrade from current bucket */
  isDowngrade?: boolean;
  /** Callback when user clicks the action button */
  onSelect?: () => void;
  /** Whether the action is loading */
  isLoading?: boolean;
  /** Additional class names */
  className?: string;
}

// =============================================================================
// Benefit Item with Tooltip
// =============================================================================

interface BenefitItemProps {
  label: string;
  tooltip: string;
  badge?: string;
  isDisabled?: boolean;
}

function BenefitItemWithTooltip({ label, tooltip, badge, isDisabled }: BenefitItemProps) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Tooltip content={tooltip}>
        <span
          className={cn(
            'cursor-help border-b border-dotted',
            isDisabled
              ? 'border-gray-300 text-gray-400'
              : 'border-gray-400 text-gray-700 hover:border-gray-600'
          )}
        >
          {label}
        </span>
      </Tooltip>
      {badge && (
        <span
          className={cn(
            'text-[9px] font-semibold px-1 py-px rounded uppercase leading-tight flex-shrink-0',
            badge === 'POPULAR' && 'bg-brand-100 text-brand-700',
            badge === 'EXCLUSIVE' && 'bg-purple-100 text-purple-700',
            badge === 'GUARANTEED' && 'bg-emerald-100 text-emerald-700 border border-emerald-200',
            badge === 'NEW' && 'bg-amber-100 text-amber-700'
          )}
        >
          {badge}
        </span>
      )}
    </span>
  );
}

// =============================================================================
// Component
// =============================================================================

export function PlanCardV2({
  plan,
  interval,
  bucket,
  isCurrentPlan = false,
  isHighlighted = false,
  isDisabled = false,
  isEligibleForIntro = true,
  isDowngrade = false,
  onSelect,
  isLoading = false,
  className,
}: PlanCardV2Props) {
  const benefitBuckets = getPlanBenefitBuckets(plan.id);
  const isFree = plan.id === 'free';
  const isEnterprise = plan.id === 'enterprise';

  // Compute button disabled state
  const buttonDisabled = isCurrentPlan || isDisabled || isFree || isLoading;

  // Calculate pricing
  const getDisplayPricing = () => {
    if (isFree) {
      return { main: '$0', sub: '/month', isIntro: false, showTopLine: true, topLine: '' };
    }

    if (isEnterprise) {
      // Enterprise: always show "starts at $99.99/mo" without discount
      return {
        main: '$99.99',
        sub: '/mo',
        isIntro: false,
        showTopLine: true,
        topLine: 'starts at',
      };
    }

    if (!bucket) {
      return { main: '—', sub: '', isIntro: false, showTopLine: true, topLine: '' };
    }

    const monthlyPrice = bucket.monthlyPriceCents;
    const annualPrice = bucket.annualPriceCents;

    if (interval === 'annual') {
      // Show annual as monthly equivalent
      const monthlyEquivalent = Math.round(annualPrice / 12);

      if (isEligibleForIntro) {
        const introMonthly = calculateIntroPrice(monthlyEquivalent);
        const savings = calculateSavings(monthlyEquivalent * 12, introMonthly * 12);

        return {
          main: formatPrice(introMonthly),
          sub: '/mo',
          billedAs: `billed yearly`,
          normalPrice: formatPrice(monthlyEquivalent),
          savings: formatPrice(savings) + ' savings*',
          isIntro: true,
          showTopLine: true,
          topLine: `normally ${formatPrice(monthlyEquivalent)}/mo`,
          topLineStrikethrough: true,
        };
      }

      // For existing subscribers, show annual vs monthly savings
      const annualSavings = calculateSavings(monthlyPrice * 12, annualPrice);

      return {
        main: formatPrice(monthlyEquivalent),
        sub: '/mo',
        billedAs: 'billed yearly',
        isIntro: false,
        showTopLine: true,
        topLine: `normally ${formatPrice(monthlyPrice)}/mo`,
        topLineStrikethrough: true,
        savings: formatPrice(annualSavings) + ' savings',
      };
    }

    // Monthly - no intro discount (coupon only applies to annual)
    return {
      main: formatPrice(monthlyPrice),
      sub: '/mo',
      isIntro: false,
      showTopLine: true,
      topLine: '',
    };
  };

  const pricing = getDisplayPricing();

  // Determine button text
  const getButtonText = () => {
    if (isCurrentPlan) return 'Current Plan';
    if (isFree) return 'Start for Free';
    if (isEnterprise) return 'Contact Sales';
    if (isDisabled) return 'Not Available';
    if (isDowngrade) return 'Downgrade';
    return 'Upgrade Now';
  };

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-2xl border bg-white transition-all',
        isHighlighted && !isDisabled && 'border-brand-500 ring-2 ring-brand-500',
        isDisabled && 'opacity-50 bg-gray-50',
        !isHighlighted && !isDisabled && 'border-gray-200 hover:shadow-md',
        className
      )}
    >
      {/* Highlight badge */}
      {plan.tagline && !isDisabled && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-brand-600 text-white whitespace-nowrap">
            {plan.tagline}
          </span>
        </div>
      )}

      <div className="p-5 flex flex-col h-full">
        {/* Fixed height top section for alignment */}
        <div className="flex flex-col">
          {/* Plan Name & Description - fixed height */}
          <div className="h-[72px] mb-4">
            <h3
              className={cn(
                'text-lg font-semibold',
                isDisabled ? 'text-gray-400' : 'text-gray-900'
              )}
            >
              {plan.name}
            </h3>
            <p
              className={cn(
                'text-sm mt-1 line-clamp-2',
                isDisabled ? 'text-gray-400' : 'text-gray-500'
              )}
            >
              {plan.description}
            </p>
          </div>

          {/* Pricing - fixed height container */}
          <div className="h-[100px] mb-4">
            {/* Top line (normally $X or starts at) - fixed height */}
            <div className="h-5 mb-1">
              {pricing.topLine && (
                <span
                  className={cn(
                    'text-sm text-gray-400',
                    pricing.topLineStrikethrough && 'line-through'
                  )}
                >
                  {pricing.topLine}
                </span>
              )}
            </div>

            {/* Main price */}
            <div className="flex items-baseline gap-1">
              <span
                className={cn(
                  'text-3xl font-bold',
                  isDisabled ? 'text-gray-400' : 'text-gray-900'
                )}
              >
                {pricing.main}
              </span>
              <span
                className={cn(
                  'text-sm',
                  isDisabled ? 'text-gray-400' : 'text-gray-500'
                )}
              >
                {pricing.sub}
              </span>
            </div>

            {/* Billed as / Savings - fixed height container */}
            <div className="h-10 mt-1">
              {pricing.billedAs && (
                <p
                  className={cn(
                    'text-xs',
                    isDisabled ? 'text-gray-400' : 'text-gray-500'
                  )}
                >
                  {pricing.billedAs}
                </p>
              )}
              {pricing.savings && (
                <p className="text-xs text-success-600 font-medium">
                  {pricing.savings}
                </p>
              )}
            </div>
          </div>

          {/* CTA Button - always at same position */}
          <Button
            variant={isHighlighted && !isDisabled ? 'primary' : isCurrentPlan ? 'ghost' : 'secondary'}
            className="w-full"
            onClick={onSelect}
            disabled={buttonDisabled}
          >
            {isLoading ? 'Loading...' : getButtonText()}
          </Button>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 my-4" />

        {/* Inherit From Line */}
        {benefitBuckets?.inheritFrom && (
          <p
            className={cn(
              'text-sm font-medium mb-3',
              isDisabled ? 'text-gray-400' : 'text-gray-700'
            )}
          >
            {benefitBuckets.inheritFrom}
          </p>
        )}

        {/* Categorized Benefits */}
        <div className="space-y-5 flex-grow">
          {benefitBuckets?.categories.map((category, catIdx) => (
            <div key={catIdx}>
              <h4
                className={cn(
                  'text-xs font-semibold uppercase tracking-wide mb-2',
                  isDisabled ? 'text-gray-400' : 'text-gray-500'
                )}
              >
                {category.header}
              </h4>
              <ul className="space-y-1.5">
                {category.items.map((item, itemIdx) => (
                  <li
                    key={itemIdx}
                    className={cn(
                      'text-sm',
                      isDisabled ? 'text-gray-400' : 'text-gray-600'
                    )}
                  >
                    <BenefitItemWithTooltip
                      label={item.label}
                      tooltip={item.tooltip}
                      badge={item.badge}
                      isDisabled={isDisabled}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Compare Link */}
        <button
          type="button"
          className={cn(
            'mt-4 text-sm text-center',
            isDisabled
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-brand-600 hover:text-brand-700'
          )}
          onClick={() => {
            if (!isDisabled) {
              document.getElementById('feature-comparison')?.scrollIntoView({
                behavior: 'smooth',
              });
            }
          }}
          disabled={isDisabled}
        >
          See all features ↓
        </button>
      </div>
    </div>
  );
}
